import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  Squares2X2Icon,
  ListBulletIcon,
  UserGroupIcon,
  UserPlusIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "sonner";
import { Button, Select } from "@/shared/ui";
import { useCountries } from "@/hooks/useCountries";
import { ClientsTable } from "../components/ClientsTable";
import { ClientCard } from "../components/ClientCard";
import { CreateClientModal } from "../components/CreateClientModal";
import { EditClientModal } from "../components/EditClientModal";
import { ListPageSkeleton } from "../../../components/skeletons";
import {
  useClients,
  ClientWithProfile,
  FilterStatus,
} from "../hooks/useClients";

type ViewMode = "grid" | "table";

export const ClientsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { countries } = useCountries();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] =
    useState<ClientWithProfile | null>(null);

  // Hook
  const {
    clients,
    stats,
    loading,
    refreshing,
    refresh,
    filters: {
      searchQuery,
      setSearchQuery,
      statusFilter,
      setStatusFilter,
      clientTypeFilter,
      setClientTypeFilter,
      countryFilter,
      setCountryFilter,
    },
  } = useClients({ lawyerId: user?.id });

  const handleRefresh = () => {
    refresh();
  };

  const handleViewClient = (clientId: string) => {
    navigate(`/dashboard/lawyer/clients/${clientId}`);
  };

  const handleEditClient = (client: ClientWithProfile) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refresh();
    toast.success(
      t("lawyer.clients.createSuccess", "Cliente creado exitosamente")
    );
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
    refresh();
    toast.success(
      t("lawyer.clients.editSuccess", "Cliente actualizado exitosamente")
    );
  };

  if (loading && clients.length === 0) {
    return <ListPageSkeleton />;
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-juridico">
                {t("lawyer.clients.title", "Clientes")}
              </h1>
              <p className="mt-1 text-tiber-700">
                {t(
                  "lawyer.clients.subtitle",
                  "Gestiona tus clientes y sus casos"
                )}
              </p>
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="h-5 w-5 mr-2" />
              {t("lawyer.clients.addClient")}
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-tiber-100 rounded-xl">
                  <UserGroupIcon className="h-6 w-6 text-tiber-600" />
                </div>
                <div>
                  <p className="text-sm text-tiber-700">
                    {t("lawyer.clients.stats.total", "Total")}
                  </p>
                  <p className="text-2xl font-bold text-juridico">
                    {stats.total}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-atlantis-100 rounded-xl">
                  <UserGroupIcon className="h-6 w-6 text-atlantis-600" />
                </div>
                <div>
                  <p className="text-sm text-tiber-700">
                    {t("lawyer.clients.stats.active", "Activos")}
                  </p>
                  <p className="text-2xl font-bold text-juridico">
                    {stats.active}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <UserPlusIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-tiber-700">
                    {t("lawyer.clients.stats.newThisMonth", "Este mes")}
                  </p>
                  <p className="text-2xl font-bold text-juridico">
                    {stats.newThisMonth}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-tiber-100 rounded-xl">
                  <UserGroupIcon className="h-6 w-6 text-tiber-600" />
                </div>
                <div>
                  <p className="text-sm text-tiber-700">
                    {t("lawyer.clients.stats.business", "Empresas")}
                  </p>
                  <p className="text-2xl font-bold text-juridico">
                    {stats.byType?.business || 0}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Filters Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-4 mb-6"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-titan" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t(
                    "lawyer.clients.search.placeholder",
                    "Buscar por nombre o email..."
                  )}
                  className="w-full pl-10 pr-4 py-2.5 border border-tiber-300 rounded-xl focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 transition-colors"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as FilterStatus)
                }
                options={[
                  {
                    value: "all",
                    label: t(
                      "lawyer.clients.filters.status.all",
                      "Todos los estados"
                    ),
                  },
                  {
                    value: "active",
                    label: t("lawyer.clients.filters.status.active", "Activos"),
                  },
                  {
                    value: "inactive",
                    label: t(
                      "lawyer.clients.filters.status.inactive",
                      "Inactivos"
                    ),
                  },
                ]}
              />

              <Select
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value)}
                options={[
                  {
                    value: "",
                    label: t(
                      "lawyer.clients.filters.allTypes",
                      "Todos los tipos"
                    ),
                  },
                  {
                    value: "individual",
                    label: t("lawyer.clientTypes.individual", "Persona física"),
                  },
                  {
                    value: "business",
                    label: t("lawyer.clientTypes.business", "Empresa"),
                  },
                  {
                    value: "nonprofit",
                    label: t(
                      "lawyer.clientTypes.nonprofit",
                      "Sin fines de lucro"
                    ),
                  },
                ]}
              />

              <Select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                options={[
                  {
                    value: "",
                    label: t(
                      "lawyer.clients.filters.country.all",
                      "Todos los países"
                    ),
                  },
                  ...countries.map((c) => ({
                    value: c.code,
                    label: i18n.language === "es" ? c.nameEs : c.nameEn,
                  })),
                ]}
              />

              {/* View Mode Toggle */}
              <div className="flex items-center bg-romance-50 rounded-xl p-1">
                <button
                  onClick={() => setViewMode("table")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "table"
                      ? "bg-white text-tiber-600 shadow-sm"
                      : "text-titan hover:text-tiber-700"
                  }`}
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === "grid"
                      ? "bg-white text-tiber-600 shadow-sm"
                      : "text-titan hover:text-tiber-700"
                  }`}
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2.5 bg-romance-50 hover:bg-tiber-100 rounded-xl transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon
                  className={`h-5 w-5 text-tiber-700 ${refreshing ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {clients.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-tiber-200 p-12 text-center">
              <UserGroupIcon className="h-16 w-16 text-titan mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-juridico mb-2">
                {searchQuery ||
                statusFilter !== "all" ||
                clientTypeFilter ||
                countryFilter
                  ? t(
                      "lawyer.clients.empty.title",
                      "No se encontraron clientes"
                    )
                  : t("lawyer.clients.empty.title", "Aún no tienes clientes")}
              </h3>
              <p className="text-tiber-700 mb-6">
                {searchQuery ||
                statusFilter !== "all" ||
                clientTypeFilter ||
                countryFilter
                  ? t(
                      "lawyer.clients.empty.filteredDescription",
                      "Intenta con otros filtros de búsqueda"
                    )
                  : t(
                      "lawyer.clients.empty.description",
                      "Comienza agregando tu primer cliente"
                    )}
              </p>
              {!searchQuery &&
                statusFilter === "all" &&
                !clientTypeFilter &&
                !countryFilter && (
                  <Button onClick={() => setIsCreateModalOpen(true)}>
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {t("lawyer.clients.addClient", "Nuevo cliente")}
                  </Button>
                )}
            </div>
          ) : viewMode === "table" ? (
            <ClientsTable
              clients={clients}
              onView={handleViewClient}
              onEdit={handleEditClient}
              loading={loading && clients.length > 0}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clients.map((client, index) => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ClientCard
                    client={client}
                    onView={() => handleViewClient(client.id)}
                    onEdit={() => handleEditClient(client)}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        lawyerId={user?.id || ""}
      />

      {selectedClient && (
        <EditClientModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
          }}
          onSuccess={handleEditSuccess}
          client={selectedClient}
        />
      )}
    </div>
  );
};
